import React, {useContext, useEffect, useRef, useState} from "react";
import Error from "../../../../Alerts/Error";
import {fileTypeFromBlob} from 'file-type';
import Spinner from "../../../../Spinner";
import { authContext } from "../../../../../context/auth";
import filterResponse from "../../../../../filterResponse";
import {extname} from "../../../../../util/path";
import {toDisplayableImage} from "../../../../../util/heic";
import { queuePositionLabel } from "../../../../../util/uploadQueue";
import CancelUpload, {useUploadCancel} from "./CancelUpload";
import { claimSelection, releaseClaim, uploadOwner, DUPLICATE_IN_ENTRY_MESSAGE } from "../../../../../util/duplicateFiles";

// Bounded rather than shown at the picture's own size: a phone photo rendered full size filled the
// whole page, and an entry page holds one of these per file. Same box the Review page previews use.
const PREVIEW_STYLE = {maxHeight: "150px", maxWidth: "100%", width: "auto", height: "auto"};

export default function Image({formId, uploadFile, fileName, setFile, fileKey, file}) {
    const fileRef = useRef();
    const imageRef = useRef();
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(false);
    const upload = useUploadCancel();

    // These inputs show a plain spinner rather than a progress bar, which says nothing about a
    // file that has not started yet. On an entry with dozens of inputs only a couple upload at a
    // time, so a small file picked last can wait a while behind the big ones.
    const [queuePosition, setQueuePosition] = useState(null);
    const reportQueuePosition = (loaded, total, phase, position) => setQueuePosition(
        prev => {
            const next = phase === "queued" ? position : null;
            return prev === next ? prev : next; // React bails out, so the upload's own ticks don't re-render this
        },
    );

    async function onChange() {
        // Held here rather than read off the element again after each await: one upload runs at a
        // time and the input is disabled while it does, but the checks below happen before that.
        const chosen = fileRef.current.files[0];
        if (!chosen) {
            return;
        }

        // Turned away the moment it is picked rather than when the queue gets round to hashing it:
        // a file waits minutes for its turn, and the second copy is picked during that wait, while
        // the first is still being checked — which is how this was reported (DEV-3191).
        const owner = uploadOwner(formId, file.id);
        if (!claimSelection(chosen, owner)) {
            fileRef.current.value = null;
            return setErrors([DUPLICATE_IN_ENTRY_MESSAGE]);
        }

        // A file the checks below turn away is not this input's after all, so it must stop being
        // held against the others. One that reaches the upload keeps its claim, which the upload
        // itself releases if it fails.
        let reachedUpload = false;

        try {
            if (!fileKey) {
                imageRef.current.classList.add("d-none");
            }

            setLoading(true);
            setErrors([]);
            const signal = upload.begin();

            let {size, name} = chosen;
            size /= 1000000;

            if (size > Number(file.properties.maxFileSize)) {
                setErrors([`File exceeded maximum file size of ${file.properties.maxFileSize} MB`]);
                fileRef.current.value = null;
            }
            else if (size < Number(file.properties.minFileSize)) {
                setErrors([`File does not have the minimum file size of ${file.properties.minFileSize} MB`]);
                fileRef.current.value = null;
            }
            else {
                const fileType = await fileTypeFromBlob(chosen);
                if (fileType !== undefined && !fileType.mime.startsWith('image')) {
                    fileRef.current.value = null;
                    return setErrors(["This is not an image file."]);
                }
                // check for the correct file extension
                if (fileType && !file.properties.extensions.includes('.' + fileType.ext)) {
                    fileRef.current.value = null;
                    return setErrors(["The extension of your file is not allowed, the allowed extensions are: " + file.properties.extensions.toString()]);
                }
                //probably a text file then
                else if (!fileType) {
                    fileRef.current.value = null;
                    return setErrors(["This is not an image file."]);
                }
                else if (!file.properties.extensions.includes(extname(name))) {
                    if (!extname(name)) {
                        name += '.' + fileType.ext;
                    }
                    else {
                        name = name.split('.').slice(0, -1).join('.') + '.' + fileType.ext
                    }
                }

                let toUpload = chosen;

                if (chosen.name !== name) {
                    const blob = new Blob([chosen], {type: chosen.type});
                    toUpload = new File([blob], name, {type: chosen.type});
                }

                // The answer keeps the name the contributor picked even when the stored object had
                // its extension corrected above.
                setQueuePosition(null); // a place in line left over from the previous pick is not this one's
                reachedUpload = true;
                const key = await uploadFile(toUpload, undefined, reportQueuePosition, signal);

                if (key) {
                    setFile(key, chosen.name);
                }
                else if (fileKey) {
                    fileRef.current.value = null;
                }
                else {
                    fileRef.current.value = null;
                    setFile('', '');
                }
            }
        }
        catch (e) {
            // Anything unexpected still has to say so. Clearing the input with no message leaves the
            // contributor looking at an empty field, assuming they did something wrong.
            console.error(e);
            fileRef.current.value = null;
            setErrors(["Something went wrong while adding your image. Please try again, and let us know if it keeps happening."]);
        }
        finally {
            if (!reachedUpload) {
                releaseClaim(owner);
            }
            upload.end();
            setLoading(false);
        }
    }

    useEffect(() => {
        let isMounted = true;
        let objectUrl = null;
        const abortController = new AbortController();
        (async () => {
            setLoading(true);

            try {
                if (fileKey) {
                    // Always fetch and sniff the bytes: the key keeps whatever extension the
                    // uploader's device used, so an iPhone's IMG_0001.HEIC has to be caught by
                    // its header rather than by the file name (DEV-3002).
                    const response = await fetch(process.env.REACT_APP_ENDPOINT + '/files/' + fileKey, {
                        credentials: "include",
                        signal: abortController.signal
                    });
                    await filterResponse(response);
                    const blob = await response.blob();

                    objectUrl = URL.createObjectURL(await toDisplayableImage(blob));
                    isMounted && (imageRef.current.src = objectUrl);
                    isMounted && imageRef.current.classList.remove("d-none");

                    const myFile = new File(['preview'], fileName);
                    const dataTransfer = new DataTransfer();
                    dataTransfer.items.add(myFile);
                    isMounted && (fileRef.current.files = dataTransfer.files);
                }
                else {
                    isMounted && imageRef.current.classList.add("d-none");
                    isMounted && (fileRef.current.value = null);
                }
            }
            catch (e) {
                console.error(e);
                isMounted && imageRef.current.classList.add("d-none");
            }

            isMounted && setLoading(false);
        })();

        return () => {
            isMounted = false;
            abortController.abort();
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [fileKey]);

    return (
        <div className={"overflow-x-auto"}>
                <label htmlFor={formId + file.id} className={"form-label"}>{file.name}:</label>
                <input disabled={loading} id={formId + file.id} form={formId} required={true} ref={fileRef} onChange={onChange} type={"file"} className={"form-control"} name={file.name}
                       accept={file.properties.extensions.toString()}/>
            {loading &&
                <div className="mt-3">
                 <Spinner />
                 {queuePosition > 0 && <small className="text-muted ms-2">{queuePositionLabel(queuePosition)}</small>}
                 {upload.active && <div><CancelUpload onCancel={upload.cancel} /></div>}
            </div>}

            <img style={PREVIEW_STYLE} ref={imageRef} className={"d-none"} alt={fileName}/>

            {/* Last, so a refusal reads as this input's and not the next one's. */}
            {errors.map((value, index) => {
                return <Error key={index} message={value} />;
            })}
        </div>
    );
}
